import React, { useEffect, useState } from 'react';

import { Row, Col, Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useUpdateEffect } from 'react-use';

import { useAppSelector } from 'hooks';
import { ReferenceRange } from 'components/UI';
import SectionTitle from 'components/UI/SectionTitle';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { getClassWeight } from 'utils/Measurements';
import { DropdownGroup } from 'components/Forms';
import { isPatientArchived } from 'store/patient/reducer';

import AddEditReferenceRange from './AddEditReferenceRange';

interface IReferenceRangesProps {
  referenceRanges: ICarePlanReferenceRange[] | undefined;
  carePlanTemplate?: ICarePlanTemplate;
  getCarePlan: any;
}

const ReferenceRanges = ({
  referenceRanges,
  carePlanTemplate,
  getCarePlan
}: IReferenceRangesProps) => {
  const intl = useIntl();
  const patient = useAppSelector(({ patient }) => patient?.data);
  const _isPatientArchived = useAppSelector(isPatientArchived);
  const [_referenceRanges, setReferenceRanges] = useState(referenceRanges || []);
  const [addEditModal, setAddEditModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selected, setSelected] = useState<ICarePlanReferenceRange>();
  const alreadyUsed = [..._referenceRanges.map((x) => x.measurement_class)];
  const isTemplateGlobal = carePlanTemplate?.template_type === 'GLOBAL';

  const sortedReferenceRanges = _referenceRanges.sort((a, b) => {
    return getClassWeight(a.measurement_class) - getClassWeight(b.measurement_class);
  });

  const [{ loading: postLoading, error: postError }, post] = useAxios(
    {
      url: carePlanTemplate
        ? '/clinician/2.0.0/CarePlanTemplate'
        : `/clinician/Patient/${patient?.uid}/CarePlan`,
      method: 'POST'
    },
    { manual: true }
  );

  const handleDelete = () => {
    post({
      data: {
        referenceRanges: [
          {
            edit_status: 'deleted',
            source: selected?.source,
            measurement_class: selected?.measurement_class,
            reference_range_id: selected?.reference_range_id
          }
        ],
        carePlanTemplate
      }
    }).then(() => {
      setReferenceRanges((prevState) =>
        prevState.filter((x) => x.measurement_class !== selected?.measurement_class)
      );
      setDeleteModal(false);
    });
  };

  const handleAction = (action: 'edit' | 'delete', item: ICarePlanReferenceRange) => {
    setSelected(item);
    action === 'edit' ? setAddEditModal(true) : setDeleteModal(true);
  };

  useUpdateEffect(() => {
    if (!addEditModal && !deleteModal) setSelected(undefined);
  }, [addEditModal, deleteModal]);

  useEffect(() => {
    setReferenceRanges(referenceRanges || []);
  }, [referenceRanges]);

  return (
    <div className="mb-5" data-test="careplan-reference-ranges-section">
      <div className="d-flex justify-content-between align-items-center">
        <SectionTitle
          dataTestId="careplan-reference-ranges-title"
          label={intl.formatMessage({ id: 'referenceRangesKey' })}
        />
        <AddEditReferenceRange
          referenceRange={selected}
          setReferenceRanges={setReferenceRanges}
          show={addEditModal}
          setModal={setAddEditModal}
          ignoreClasses={alreadyUsed}
          carePlanTemplate={carePlanTemplate}
          getCarePlan={getCarePlan}
        />
        {!isTemplateGlobal && (
          <Button
            data-test="careplan-reference-ranges-btn"
            variant="link"
            size="sm"
            onClick={() => setAddEditModal(true)}
            disabled={_isPatientArchived}
          >
            + {intl.formatMessage({ id: 'referenceRangesKey' })}
          </Button>
        )}
      </div>
      <Modal
        data-test="delete-reference-range-modal"
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
      >
        <Modal.Header className="align-items-center">
          <Modal.Title>
            {intl.formatMessage({ id: 'deleteKey' }) +
              ' ' +
              intl.formatMessage({ id: 'referenceRangesKey' })}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selected && (
            <div className="ranger p-3">
              <div className="d-flex align-items-center  justify-content-between">
                <span className="font-weight-bolder small">
                  {intl.formatMessage({ id: selected.measurement_class })}
                </span>
              </div>
              <ReferenceRange data={selected} />
            </div>
          )}
          {postLoading && <Loading className="mt-4" />}
          {postError && <Error className="mt-4" retry={handleDelete} dismissible={false} />}
        </Modal.Body>
        <Modal.Footer>
          <Button
            data-test="cancel-delete-reference-range-btn"
            variant="link"
            onClick={() => setDeleteModal(false)}
          >
            {intl.formatMessage({ id: 'Cancel' })}
          </Button>
          <Button
            data-test="confirm-delete-reference-range-btn"
            variant="danger"
            onClick={handleDelete}
          >
            {intl.formatMessage({ id: 'deleteKey' })}
          </Button>
        </Modal.Footer>
      </Modal>
      {_referenceRanges.length > 0 ? (
        <Row>
          {sortedReferenceRanges.map((referenceRange) => (
            <Col
              data-test={`reference-range-${referenceRange.measurement_class}`}
              xl={6}
              lg={12}
              key={referenceRange.measurement_class}
              className="mb-3"
            >
              <div className="ranger p-3" data-test="reference-range-block">
                <div className="d-flex align-items-center  justify-content-between">
                  <span className="font-weight-bolder small">
                    {intl.formatMessage({
                      id: referenceRange.measurement_class
                    })}
                  </span>
                  {!isTemplateGlobal && (
                    <DropdownGroup
                      dataTestId={`reference-range-${referenceRange.measurement_class}`}
                      onShowEditModal={() => handleAction('edit', referenceRange)}
                      onShowDeleteModal={() => handleAction('delete', referenceRange)}
                      disabled={_isPatientArchived}
                    />
                  )}
                </div>
                <ReferenceRange data={referenceRange} />
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <div
          data-test="careplan-reference-ranges-empty"
          className="bg-light text-center p-3 font-weight-bolder"
        >
          {intl.formatMessage({ id: 'noRangesSet' })}
        </div>
      )}
    </div>
  );
};

export default ReferenceRanges;
